/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import React, { ChangeEvent, PureComponent, ReactComponentElement } from 'react';
import _ from 'lodash';

// ours
import ReportDrawer from './ReportDrawer';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';
import { ReportViewModel } from 'src/types/view-models/report.view.model';
import { TimePeriod } from 'src/types/time-period';
import Section from '../section/Section';
import { ReportType } from 'src/types/report-type.enum';
import InfoCallout from '../callout/InfoCallout';
import ReportBar from '../report-progress-bar/ReportBar';
import ReportService from '../../services/report.service';
import { BaseAssetModel, BaseDebtModel, DebtReportModel } from 'src/types/asset-debt.model';
import { AdjustmentModel } from 'src/types/adjusment.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import NavigationService from '../../services/navigation.service';
import { RealEstateModel } from 'src/types/real-estate.model';
import { InvestmentModel } from 'src/types/investment.model';
import { DebtModel } from 'src/types/debt.model';
import { MonteCarloEstimate } from 'src/types/monte-carlo.enum';

interface AssetDebtDrawerState {
    monteCarlo: MonteCarloEstimate;
}

export default class AssetDebtDrawer extends PureComponent<ReportViewModel, AssetDebtDrawerState> {
    public override state = {
        monteCarlo: MonteCarloEstimate.AVERAGE
    };

    public override render(): ReactComponentElement<any> {
        const notPast: boolean = this.props.drawerProps.period !== TimePeriod.PAST;

        // assets
        const [liquidAssetsTotal, illiquidAssetsTotal]: number[] = ReportService.getLiquidAndIlliquidAssets(
            this.props.drawerProps.cashFlowDetails,
            this.state.monteCarlo
        );
        const assetAdjustments: AdjustmentModel[] = _.get(this, 'props.drawerProps.cashFlowDetails.assets.adjustments');
        const assetAdjustment: number = _.sumBy(assetAdjustments, 'amount');
        const liquidAssetsTitle: string = LiteralsService.getLiteral('AssetDebtDrawer.liquidAssetsTitle', [notPast]);
        const liquidAssetsBars: JSX.Element[] = this.getAssetBars(
            true,
            notPast,
            liquidAssetsTotal + Number(this.props.drawerProps.freeCash)
        );
        const restrictedAssetsTitle: string = LiteralsService.getLiteral('AssetDebtDrawer.restrictedAssetsTitle', [
            notPast
        ]);
        const restrictedAssetsBars: JSX.Element[] = this.getAssetBars(
            false,
            notPast,
            illiquidAssetsTotal + assetAdjustment,
            assetAdjustments
        );
        const assetCallout: JSX.Element =
            this.props.drawerProps.period !== TimePeriod.PAST ? (
                <InfoCallout text={LiteralsService.getLiteral('AssetDebtDrawer.restrictedAssetsInfo')} noMargin />
            ) : null;

        // debts
        const debtAdjustments: AdjustmentModel[] = _.get(this, 'props.drawerProps.cashFlowDetails.debts.adjustments');
        const debtTitle: string = LiteralsService.getLiteral('AssetDebtDrawer.debtTitle', [notPast]);
        const debtBars: JSX.Element[] = this.getDebtBars(debtAdjustments);
        const debtCallout: JSX.Element =
            this.props.drawerProps.period !== TimePeriod.PAST ? (
                <InfoCallout text={LiteralsService.getLiteral('AssetDebtDrawer.debtInfo')} noMargin />
            ) : null;

        return (
            <ReportDrawer
                drawerProps={this.props.drawerProps}
                title={LiteralsService.getLiteral('AssetDebtDrawer.title', [this.props.drawerProps.dateString])}
                reportType={ReportType.ASSET_AND_DEBT}
                monteCarlo={this.state.monteCarlo}
                handleClick={this.handleClick.bind(this)}
            >
                {liquidAssetsBars?.length > 0 && (
                    <Section
                        title={liquidAssetsTitle}
                        tooltipDescription={LiteralsService.getLiteral('AssetDebtDrawer.liquidAssetsTooltip')}
                        value={liquidAssetsTotal + Number(this.props.drawerProps.freeCash)}
                    >
                        {liquidAssetsBars}
                    </Section>
                )}
                {restrictedAssetsBars?.length > 0 && (
                    <Section
                        title={restrictedAssetsTitle}
                        tooltipDescription={LiteralsService.getLiteral('AssetDebtDrawer.restrictedAssetsTooltip')}
                        value={illiquidAssetsTotal + assetAdjustment}
                        extraComp={assetCallout}
                    >
                        {restrictedAssetsBars}
                    </Section>
                )}
                {debtBars?.length > 0 && (
                    <Section
                        title={debtTitle}
                        tooltipDescription={LiteralsService.getLiteral('AssetDebtDrawer.debtSectionTooltip')}
                        value={this.props.drawerProps.cashFlowDetails.debts.amount}
                        extraComp={debtCallout}
                    >
                        {this.getDebtBars(debtAdjustments)}
                    </Section>
                )}
            </ReportDrawer>
        );
    }

    private getAssetBars(
        liquid: boolean = true,
        notPast: boolean,
        total: number,
        adjustments?: AdjustmentModel[]
    ): JSX.Element[] {
        let colorMap: Map<string, string> = new Map<string, string>();
        let assets: Array<AdjustmentModel | BaseAssetModel>;
        if (liquid) {
            assets = this.props.drawerProps.liquidAssets;
        } else if (adjustments) {
            assets = _(adjustments)
                .concat(this.props.drawerProps.restrictedAssets)
                .orderBy((ge) => ge.amount, ['desc'])
                .value();
        } else {
            assets = this.props.drawerProps.restrictedAssets;
        }

        const bases: Array<AdjustmentModel | BaseAssetModel> = assets.map((base: AdjustmentModel | BaseAssetModel) => {
            base.percentage = (this.getAmount(base) / total) * 100;
            return base;
        });

        const bars: JSX.Element[] = bases.map((base: AdjustmentModel | BaseAssetModel, index: number) => {
            const key: string = liquid ? `liquid-asset-bar-${index}` : `restricted-asset-bar-${index}`;
            let [cardImage, cardName, onClick] = ReportService.getCardDetails(
                adjustments,
                base,
                this.props.drawerProps.cards,
                this.props.drawerProps.history
            );

            const amount = this.getAmount(base);
            if (!amount) return null;

            if (!onClick) {
                onClick = () => {
                    void this.openAsset(base);
                };
            }

            const title: string = cardName || base.name;
            colorMap = ReportService.updateColorMapForTitle(colorMap, title);

            return (
                <ReportBar
                    title={title}
                    key={key}
                    value={FormatService.formatCurrency(amount)}
                    percentage={FormatService.formatPercentage(base.percentage)}
                    color={colorMap.get(title)}
                    img={cardImage}
                    onClick={onClick}
                ></ReportBar>
            );
        });

        if (liquid) {
            const freeCashPercentage: number = (this.props.drawerProps.freeCash / total) * 100;

            if (notPast) {
                const title: string = LiteralsService.getLiteral('AssetDebtDrawer.baselineSavings');
                colorMap = ReportService.updateColorMapForTitle(colorMap, title);

                bars.unshift(
                    <ReportBar
                        title={title}
                        key={'liquid-asset-bar-baseline'}
                        value={FormatService.formatCurrency(this.props.drawerProps.freeCash)}
                        percentage={FormatService.formatPercentage(freeCashPercentage)}
                        color={colorMap.get(title)}
                        tooltipDescription={LiteralsService.getLiteral('ShowWork2.baseline')}
                        forceBlue={true}
                    ></ReportBar>
                );
            }
        }
        return bars;
    }

    private getDebtBars(adjustments?: any[]): JSX.Element[] {
        let colorMap: Map<string, string> = new Map<string, string>();
        const debts: DebtReportModel = _.get(this, 'props.drawerProps.cashFlowDetails.debts');
        let combined: Array<AdjustmentModel | BaseDebtModel>;
        if (adjustments) {
            combined = _(adjustments)
                .concat(debts.bases)
                .orderBy((ge) => ge.amount, ['desc'])
                .value();
        }

        const bases: Array<AdjustmentModel | BaseDebtModel> = combined.map((base: AdjustmentModel | BaseDebtModel) => {
            base.percentage = (base.amount / debts.amount) * 100;
            return base;
        });

        return bases.map((base: AdjustmentModel | BaseDebtModel, index: number) => {
            const key: string = `debt-bar-${index}`;
            let [cardImage, cardName, onClick] = ReportService.getCardDetails(
                adjustments,
                base,
                this.props.drawerProps.cards,
                this.props.drawerProps.history
            );

            const debt: DebtModel = _.find(this.props.user.debts, { id: base.id });
            if (debt && !onClick) {
                onClick = () => {
                    this.props.handleEditDebt(debt);
                };
            }

            const title: string = cardName || base.name;
            colorMap = ReportService.updateColorMapForTitle(colorMap, title);

            return (
                <ReportBar
                    title={title}
                    key={key}
                    value={FormatService.formatCurrency(base.amount)}
                    percentage={FormatService.formatPercentage(base.percentage)}
                    color={colorMap.get(title)}
                    img={cardImage}
                    onClick={onClick}
                ></ReportBar>
            );
        });
    }

    private getAmount(anAsset: any): number {
        if (anAsset.fixed) {
            return anAsset.amount;
        }

        switch (this.state.monteCarlo) {
            case MonteCarloEstimate.PESSIMISTIC:
                return anAsset.amountLow;

            case MonteCarloEstimate.AVERAGE:
                return anAsset.amount;

            case MonteCarloEstimate.OPTIMISTIC:
                return anAsset.amountHigh;
        }
    }

    private async openAsset(anAsset: any): Promise<void> {
        if (!anAsset.id) {
            return;
        }

        const holding: InvestmentHoldingModel = _.find(this.props.user.holdings, { investment_id: anAsset.id });
        if (holding) {
            await NavigationService.goTo(this.props.history, `/investment/${holding.id}`, 'Investment drawer');
            return;
        }

        const realEstate: RealEstateModel = _.find(this.props.user.realEstate, { id: anAsset.id });
        if (realEstate) {
            await NavigationService.goTo(this.props.history, `/realestate/${realEstate.id}`, 'Real Estate drawer');
            return;
        }

        const invest: InvestmentModel = _.find(this.props.user.investments, { id: anAsset.id });
        if (invest) {
            this.props.handleEditInvestment(invest);
        }
    }

    private async handleClick(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        this.setState({
            monteCarlo: event.target.value as MonteCarloEstimate
        });
    }
}
